import type { NativeSelectProps } from "@mui/material/NativeSelect"

import { Chevron } from "@components/icons"
import colors from "@constants/colors"
import {
  body14Styles,
  body16MainStyles,
  smallcapsBold14Styles,
  subtextStyles,
} from "@constants/typography"
import { ErrorMessage } from "@hookform/error-message"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import NativeSelect from "@mui/material/NativeSelect"
import React from "react"
import { useFormContext } from "react-hook-form"
import styled, { css } from "styled-components"

export const Container = styled.div<{ $error?: boolean }>`
  ${({ $error }) => css`
    width: 100%;
    padding: 0;
    border: solid 1px ${colors.dividerLineGray};
    border-radius: 2px;
    background-color: transparent;

    .MuiFormControl-root {
      width: 100%;
    }

    .MuiFormLabel-root {
      ${body16MainStyles};
      color: ${colors.mediumGray};
    }

    .MuiFormLabel-filled {
      ${smallcapsBold14Styles}
      color: ${colors.black};
    }

    .MuiNativeSelect-select {
      height: 40px;
      padding: 0 38px 0 14px;
    }

    .MuiNativeSelect-select:focus {
      background-color: transparent;
    }

    .MuiInputBase-root {
      ${body14Styles}
      color: ${$error ? colors.errorBackground : colors.mediumDarkGray};
    }

    .MuiInput-underline:before {
      border: none;
    }

    && .MuiInput-underline:hover:before {
      border: none;
    }

    .MuiInput-underline:after {
      border: none;
    }
  `}
`

const Option = styled.option``

export const IconWrapper = styled.div`
  position: absolute;
  right: 0;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  display: flex;
  transform: translateY(-1px) rotate(90deg);
  height: 24px;
  width: 24px;
  pointer-events: none;
`

const Error = styled.div`
  ${subtextStyles}
  margin-top: 12px;
  padding-left: 30px;
  color: ${colors.errorMui};
`

const IconComponent = () => (
  <IconWrapper>
    <Chevron strokeColor={colors.mediumGray} />
  </IconWrapper>
)

interface SelectInputProps extends Omit<NativeSelectProps, "error"> {
  error?: boolean | string
  helperText?: React.ReactNode
  label?: string
  name: string
  options: Array<{
    label: number | string
    value: number | string
  }>
  variant?: "filled" | "outlined" | "standard"
}

const SelectInput: React.FC<SelectInputProps> = ({
  error,
  helperText,
  label,
  name,
  onBlur,
  onChange,
  options,
  value,
  variant,
}) => {
  const { formState } = useFormContext?.() || {
    formState: { errors: {} } as any,
  }
  const id = name
  const hasError = !!error
  const errorText = typeof error === "string" ? error : undefined

  return (
    <Container $error={!!hasError}>
      <FormControl>
        {label && (
          <InputLabel htmlFor={id} variant={variant || "standard"}>
            {label}
          </InputLabel>
        )}
        <NativeSelect
          aria-describedby={hasError ? `${id}-error` : undefined}
          aria-invalid={hasError || undefined}
          IconComponent={IconComponent}
          id={id}
          inputProps={{ id, name }}
          onBlur={onBlur}
          onChange={onChange}
          value={value}
        >
          <Option aria-label="None" value="" />
          {options.map(({ label, value }) => (
            <Option key={value} value={value}>
              {label}
            </Option>
          ))}
        </NativeSelect>
      </FormControl>
      {errorText ? (
        <Error id={`${id}-error`}>{errorText}</Error>
      ) : (
        (helperText ?? (
          <ErrorMessage
            errors={(formState as any).errors || {}}
            name={name}
            render={({ message }) =>
              message ? <Error id={`${id}-error`}>{message}</Error> : null
            }
          />
        ))
      )}
    </Container>
  )
}

export const RHFSelectInput = React.forwardRef<
  HTMLSelectElement,
  SelectInputProps
>((props: SelectInputProps, ref) => (
  <SelectInput {...props} inputRef={ref as any} />
))

export default SelectInput
